import { useEffect, useState } from 'react'

/**
 * Một nhịp đồng hồ chung cho cả màn hình. Mỗi ô bàn tự chạy timer riêng thì
 * 30 cái bàn là 30 lần vẽ lại không đồng bộ — một nhịp thì cả sơ đồ nhích cùng lúc.
 */
export function useNow(intervalMs = 30_000): number {
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    const tick = () => setNow(Date.now())
    const id = setInterval(tick, intervalMs)
    // Máy tính bảng để ngủ cả tiếng, mở ra là đồng hồ phải đúng ngay.
    const onVisible = () => {
      if (document.visibilityState === 'visible') tick()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      clearInterval(id)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [intervalMs])

  return now
}

/**
 * Theo dõi một câu hỏi media query từ phía JavaScript.
 *
 * Dùng khi CSS ẩn một khoang đi và hành vi phải đổi theo, chứ không chỉ hình vẽ đổi:
 * ở màn hình hẹp, khoang đơn bên phải bị ẩn, nên chạm vào bàn phải đi thẳng sang màn
 * hình gọi món thay vì chọn một cái bàn rồi chẳng thấy gì xảy ra.
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window === 'undefined' ? false : window.matchMedia(query).matches,
  )

  useEffect(() => {
    const mq = window.matchMedia(query)
    const onChange = () => setMatches(mq.matches)
    onChange()
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [query])

  return matches
}

/**
 * Giá trị input[type=datetime-local] cho một mốc thời gian, theo giờ máy.
 *
 * Chưa có mốc nào thì lấy giờ hiện tại <b>làm tròn lên 15 phút</b>. Ô chọn giờ chỉ cho
 * bấm 00 · 15 · 30 · 45, nên một giá trị mặc định kiểu 14:25 sẽ không có trong cột phút —
 * người dùng mở ra thấy một con số mà bánh xe không chỉ tới được. Mốc đã lưu từ trước thì
 * giữ nguyên từng phút: sửa giờ của một cái hẹn đã chốt với khách là việc của người dùng,
 * không phải của cái ô nhập.
 */
export function toLocalInput(iso: string | null): string {
  const d = iso ? new Date(iso) : new Date()
  if (!iso) {
    d.setSeconds(0, 0)
    d.setMinutes(Math.ceil(d.getMinutes() / 15) * 15)
  }
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

/** Mốc giờ tròn gợi ý sẵn khi đặt bàn: lát nữa, trưa, tối, mai. */
export function quickReserveTimes(now = new Date()): { label: string; value: Date }[] {
  const at = (h: number, m = 0, addDays = 0) => {
    const d = new Date(now)
    d.setDate(d.getDate() + addDays)
    d.setHours(h, m, 0, 0)
    return d
  }

  // Làm tròn lên mốc 15 phút gần nhất, cùng nhịp với ô chọn giờ. Bấm "Sau 1 tiếng" lúc
  // 18:23 mà ra 19:23 thì con số ấy không có trong cột phút, và nó cũng chẳng tròn trịa
  // gì để đọc cho khách nghe qua điện thoại.
  const inHour = new Date(now.getTime() + 60 * 60_000)
  inHour.setSeconds(0, 0)
  inHour.setMinutes(Math.ceil(inHour.getMinutes() / 15) * 15)

  const out: { label: string; value: Date }[] = [{ label: 'Sau 1 tiếng', value: inHour }]

  if (now.getHours() < 11) out.push({ label: 'Trưa 11:30', value: at(11, 30) })
  if (now.getHours() < 18) out.push({ label: 'Tối 18:00', value: at(18) })
  if (now.getHours() < 19) out.push({ label: 'Tối 19:30', value: at(19, 30) })
  out.push({ label: 'Mai 8:00', value: at(8, 0, 1) })

  return out.slice(0, 4)
}
